// Cache clearing utility for fixing browser cache issues

export const clearAllCaches = async (): Promise<void> => {
  console.log("Starting comprehensive cache clear...");

  try {
    // 1. Clear all localStorage
    console.log("Clearing localStorage...");
    const localStorageKeys = Object.keys(localStorage);
    localStorageKeys.forEach((key) => {
      console.log(`Removing localStorage key: ${key}`);
      localStorage.removeItem(key);
    });

    // 2. Clear all sessionStorage
    console.log("Clearing sessionStorage...");
    sessionStorage.clear();

    // 3. Clear IndexedDB
    console.log("Clearing IndexedDB...");
    if ("indexedDB" in window) {
      const databases = await indexedDB.databases();
      await Promise.all(
        databases.map((db) => {
          if (db.name) {
            console.log(`Deleting IndexedDB: ${db.name}`);
            return indexedDB.deleteDatabase(db.name);
          }
        }),
      );
    }

    // 4. Clear all service worker caches
    console.log("Clearing service worker caches...");
    if ("caches" in window) {
      const cacheNames = await caches.keys();
      await Promise.all(
        cacheNames.map((cacheName) => {
          console.log(`Deleting cache: ${cacheName}`);
          return caches.delete(cacheName);
        }),
      );
    }

    // 5. Unregister all service workers
    console.log("Unregistering service workers...");
    if ("serviceWorker" in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      for (const registration of registrations) {
        console.log(`Unregistering service worker: ${registration.scope}`);
        await registration.unregister();
      }
    }

    // 6. Clear cookies for the current domain
    console.log("Clearing cookies...");
    document.cookie.split(";").forEach((cookie) => {
      const eqPos = cookie.indexOf("=");
      const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
      if (name) {
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${window.location.hostname}`;
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=.${window.location.hostname}`;
      }
    });

    console.log("Cache clear completed successfully!");
  } catch (error) {
    console.error("Error during cache clear:", error);
    throw error;
  }
};

export const clearSupabaseCache = async (): Promise<void> => {
  console.log("Clearing Supabase-specific cache...");

  try {
    // Clear all localStorage items related to Supabase
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (
        key &&
        (key.includes("supabase") ||
          key.includes("auth") ||
          key.includes("sb-") ||
          key.includes("access_token") ||
          key.includes("refresh_token"))
      ) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach((key) => {
      console.log(`Removing Supabase key: ${key}`);
      localStorage.removeItem(key);
    });

    // Clear session storage
    sessionStorage.clear();

    console.log("Supabase cache cleared!");
  } catch (error) {
    console.error("Error clearing Supabase cache:", error);
    throw error;
  }
};

export const createCacheClearButton = (): void => {
  // Create a floating button for emergency cache clear
  const button = document.createElement("button");
  button.innerHTML = "🧹 Clear Cache";
  button.style.cssText = `
    position: fixed;
    bottom: 20px;
    right: 20px;
    background: #ff4444;
    color: white;
    border: none;
    padding: 10px 20px;
    border-radius: 5px;
    cursor: pointer;
    font-weight: bold;
    z-index: 99999;
    box-shadow: 0 2px 10px rgba(0,0,0,0.3);
  `;

  button.onclick = async () => {
    if (
      confirm("This will clear all cached data and reload the page. Continue?")
    ) {
      try {
        await clearAllCaches();
        await clearSupabaseCache();
        window.location.reload();
      } catch (error) {
        console.error("Failed to clear cache:", error);
        alert("Failed to clear cache. Check console for details.");
      }
    }
  };

  document.body.appendChild(button);
};

// Auto-detect cache issues and suggest clearing
export const detectCacheIssues = (): boolean => {
  const indicators = [
    // Check for old Supabase tokens
    (() => {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.includes("supabase.auth.token")) {
          const value = localStorage.getItem(key);
          if (value) {
            try {
              const data = JSON.parse(value);
              if (data.expires_at) {
                const expiryTime = new Date(data.expires_at).getTime();
                if (expiryTime < Date.now()) {
                  console.warn("Found expired Supabase token");
                  return true;
                }
              }
            } catch (e) {
              console.warn("Found corrupted Supabase token");
              return true;
            }
          }
        }
      }
      return false;
    })(),

    // Check for conflicting auth states
    (() => {
      let authStates = 0;
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.includes("auth") || key.includes("session"))) {
          authStates++;
        }
      }
      if (authStates > 5) {
        console.warn(`Found ${authStates} auth-related items in localStorage`);
        return true;
      }
      return false;
    })(),
  ];

  return indicators.some((issue) => issue);
};

// Run on page load if cache issues detected
if (typeof window !== "undefined") {
  window.addEventListener("load", () => {
    if (detectCacheIssues()) {
      console.warn("Cache issues detected! Consider clearing cache.");
      // Optionally show the clear cache button
      if (process.env.NODE_ENV === "development") {
        createCacheClearButton();
      }
    }
  });
}
